import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronLeft,
  ClipboardList,
  Download,
  Eye,
  Minus,
  Pencil,
  Plus,
  Save,
  Upload,
  X,
} from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { CustomDropdown, SearchableDropdown } from '../../components/ui/CustomDropdown';
import type { MasterInventoryItem, MasterServiceItem } from '../../types/admin';

// ── Types ──────────────────────────────────────────────────────────────────

interface AppConfig {
  craftMyPdfTemplateId?: string;
  resendFromEmail?: string;
  resendEmailVerificationTemplateId?: string;
  resendPasswordResetTemplateId?: string;
  resendEmailChangeTemplateId?: string;
  resendInAppNotificationTemplateId?: string;
  resendUserInvitedTemplateId?: string;
  resendQuoteEmailTemplateId?: string;
  resendPaymentRequestTemplateId?: string;
  resendPlanConfirmationTemplateId?: string;
  resendPlanCancellationTemplateId?: string;
  updatedAt: string;
}

interface DemoOrg {
  id: string;
  name: string;
}

// ── JSON template ──────────────────────────────────────────────────────────

const QUOTE_TEMPLATE = JSON.stringify(
  [
    {
      type: 'commercial',
      createdAt: '2025-11-15T09:00:00.000Z',
      quoteStatus: 'booked',
      lifecycleStage: 'job',
      customerCompanyName: 'Acme Corp',
      numberOfEmployees: 120,
      inboundChannel: 'phone_call_inbound',
      discoverySource: 'google',
      pointOfContact: {
        name: 'Jane Smith',
        phone: '555-123-4567',
        email: 'jane@acmecorp.com',
        notes: 'Prefers morning calls',
      },
      movingDate: {
        preferredDate: '2026-04-15',
        deadline: '2026-04-30',
        timing: 'Work week - day',
      },
      packingDate: '2026-04-14',
      moveType: 'local',
      bindingQuote: false,
      addresses: {
        origin: {
          address: '123 Main St, Suite 400, New York, NY 10001',
          floor: '4',
          officeNumber: '400',
        },
        internalMove: false,
        destination: {
          address: '456 Broadway, Floor 12, New York, NY 10013',
          floor: '12',
          officeNumber: '1200',
        },
      },
      originLogistics: {
        strides1: 40,
        waypoint: 'Elevator',
        strides2: 25,
        loadingDock: true,
      },
      destinationLogistics: {
        strides1: 55,
        waypoint: 'Stairs',
        stories: 2,
        strides2: 30,
        loadingDock: false,
      },
      casegoodsInventory: [
        { itemName: 'Desk - Executive', quantity: 15 },
        { itemName: 'Chair - Task', quantity: 80 },
        { itemName: 'File Cabinet - Lateral', quantity: 25 },
      ],
      modularWorkstations: {
        totalWorkstations: 60,
        moverDisassembling: true,
        moverReassembling: true,
        workstationsSampled: 5,
        samples: [
          { itemName: 'Panel - 48"', quantitySampled: 12 },
        ],
      },
      scopeOfServices: [
        { serviceName: 'Disconnect & Reconnect Electronics', party: 'Customer', enabled: true },
        { serviceName: 'Pack & Unpack Personal Items', party: 'Customer', enabled: false },
        { serviceName: 'Furniture Disassembly & Reassembly', party: 'Mover', enabled: true },
      ],
      outcome: {
        reason: 'Price was competitive',
        note: 'Client appreciated our speed and professionalism',
      },
    },
    {
      type: 'residential',
      createdAt: '2025-12-01T10:30:00.000Z',
      quoteStatus: 'sent',
      lifecycleStage: 'quote',
      customerCompanyName: 'Johnson Family',
      inboundChannel: 'website_form',
      discoverySource: 'referral',
      referredBy: 'Sarah Thompson',
      pointOfContact: {
        name: 'Mark Johnson',
        phone: '555-987-6543',
        email: 'mark.johnson@email.com',
      },
      movingDate: {
        preferredDate: '2026-05-10',
        deadline: '2026-05-31',
        timing: 'Weekend',
      },
      packingDate: '2026-05-09',
      moveType: 'intrastate',
      bindingQuote: true,
      numberOfBedrooms: 4,
      squareFootage: 2800,
      originStories: 2,
      destinationStories: 1,
      originElevatorAccess: false,
      destinationElevatorAccess: false,
      addresses: {
        origin: {
          address: '789 Oak Avenue, Westfield, NJ 07090',
          residentialProperty: {
            propertyType: 'single_family',
            bedrooms: 4,
            bathrooms: 3,
            squareFootage: 2800,
            stories: 2,
            basement: true,
            garageSpaces: 2,
            pool: false,
          },
        },
        internalMove: false,
        destination: {
          address: '321 Maple Drive, Princeton, NJ 08540',
        },
      },
      originLogistics: {
        strides1: 60,
        waypoint: 'Truck',
        strides2: 0,
        loadingDock: false,
      },
      destinationLogistics: {
        strides1: 35,
        waypoint: 'Stairs',
        stories: 1,
        strides2: 20,
        loadingDock: false,
      },
      rooms: [
        { roomName: 'Master Bedroom' },
        { roomName: 'Bedroom' },
        { roomName: 'Living Room' },
        { roomName: 'Kitchen' },
        { roomName: 'Garage' },
      ],
      scopeOfServices: [
        { serviceName: 'Pack & Unpack Personal Items', party: 'Mover', enabled: true },
        { serviceName: 'Furniture Disassembly & Reassembly', party: 'Mover', enabled: true },
      ],
    },
  ],
  null,
  2,
);

// ── LLM instructions ──────────────────────────────────────────────────────

const LLM_INSTRUCTIONS = `IOMI Bulk Record Import — Data Specification
=============================================

This document describes how to fill out a JSON file of records for bulk import into the IOMI app.
The JSON file should contain an array of record objects at the top level.
Each record can be a lead, quote, or job — for either commercial or residential moves.

GENERAL STRUCTURE
-----------------
Each record in the array is a JSON object. Below are all supported fields.

REQUIRED FIELDS
---------------
- customerCompanyName (string): Name of the customer's company or household. Must not be empty.

OPTIONAL FIELDS — CORE
-----------------------

type (string)
  The type of move. One of: "commercial", "residential"
  Default: "commercial"
  Commercial moves use casegoodsInventory and modularWorkstations.
  Residential moves use rooms (auto-populated via Smart Fill logic).

createdAt (string, ISO 8601)
  The date-time this record was "created." Use this to backfill historical demo data.
  Example: "2025-09-20T14:30:00.000Z"
  If omitted, the current timestamp is used.

quoteStatus (string)
  One of: "draft", "sent", "response_received", "booked", "declined", "cancelled"
  Default: "draft"
  Notes:
    - "response_received" means the client has responded to a sent quote version
    - "booked" / "declined" are terminal statuses; pair with "outcome" (see below)

lifecycleStage (string)
  Distinguishes leads, quotes, and jobs within the same data model.
  One of: "lead", "quote", "job"
  Default: "quote"
  Notes:
    - "lead" — early-stage prospect, uses leadStatus to track progress
    - "quote" — active estimation/bidding phase
    - "job" — booked and scheduled for execution, uses jobStatus

leadStatus (string)
  Only used when lifecycleStage = "lead".
  One of: "new", "contacted", "estimate_scheduled", "not_pursuing", "lost"
  Default: "new" (when lifecycleStage is "lead")

inboundChannel (string)
  How the lead entered the system — internal tracking, not customer-facing.
  One of:
    "booking_form"         → Online booking form submission
    "website_form"         → General website contact form
    "phone_call_inbound"   → Inbound phone call
    "walk_in"              → Walk-in to the office
    "partner_broker"       → Referral from a partner or broker
    "manual_entry"         → Manually entered by staff
  For diverse demo data: vary across channels. Most common are
  "phone_call_inbound", "website_form", and "manual_entry".

discoverySource (string)
  How the customer heard about the company — customer-facing.
  One of:
    "google", "yelp", "homeadvisor_angi", "thumbtack", "nextdoor",
    "social_media", "repeat_customer", "referral", "trade_show_event", "other"
  For diverse demo data: vary across sources. "google" and "referral" are most common.

leadSource (string) — DEPRECATED, use inboundChannel + discoverySource instead.
  Legacy field, still accepted for backward compatibility. One of:
    "google", "yelp", "homeadvisor_angi", "thumbtack", "nextdoor",
    "website_form", "repeat_customer", "referral", "social_media",
    "trade_show_event", "phone_call_inbound", "partner_broker", "other"

referredBy (string)
  Who referred this lead — only relevant when discoverySource = "referral"
  (or legacy leadSource = "referral").

nextFollowUp (string, ISO date)
  Date when someone should follow up with the lead. Example: "2026-05-01"

estimatedValue (number)
  Rough dollar estimate of the move value — helps prioritize leads.

estimatedMoveDateRange (string)
  Free text for vague move dates like "June" or "last week of October".

leadLostReason (string)
  Reason the lead was lost — only set when leadStatus = "lost".

leadNotPursuingReason (string)
  Reason the lead is not being pursued — only set when leadStatus = "not_pursuing".

leadNotPursuingNote (string)
  Optional free-text note for not-pursuing reason.

jobStatus (string, optional)
  Only applies to records with lifecycleStage = "job".
  One of: "scheduled", "in_progress", "completed", "on_hold", "cancelled"

moveCompletionDate (string, ISO date)
  Actual move completion date. Only meaningful when jobStatus = "completed".
  Example: "2026-04-20"

numberOfEmployees (number)
  Total employees at the customer's company. Positive integer.
  Mainly relevant for commercial moves.

pointOfContact (object)
  Contact person at the customer's company or household.
  Fields (all optional strings):
    - name: Full name
    - phone: Phone number
    - email: Email address
    - notes: Free-form notes about the contact

movingDate (object)
  Scheduling information for the move.
  Fields:
    - preferredDate (string): ISO date, e.g. "2026-04-15"
    - deadline (string): ISO date — latest acceptable move date
    - timing (string): MUST be exactly one of:
        "Work week - day"      → 7-hour shift
        "Work week - evening"  → 5-hour shift
        "Weekend"              → 8-hour shift
      This value directly impacts the formula engine's shift length calculation.
      For diverse demo data, vary this across quotes.

packingDate (string, ISO date, optional)
  A separate day for packing before the move. Example: "2026-04-14"
  Relevant for both commercial and residential moves.

moveType (string, optional)
  Type of move based on distance. One of: "local", "intrastate", "interstate"
  Auto-populated from addresses when available, but can be overridden here.

bindingQuote (boolean, optional)
  Whether this is a binding quote (guarantees price) or non-binding (estimate).
  Default: false (non-binding)

OPTIONAL FIELDS — ADDRESSES
----------------------------

addresses (object)
  Origin and destination addresses.
  Fields:
    - origin (object):
        - address (string): Full street address. The system automatically geocodes
            this address via the Google Maps Geocoding API to obtain a Place ID,
            which is then used for transit time calculations.
            Use real, valid street addresses for accurate transit results.
        - floor (string, optional): Floor number/name
        - officeNumber (string, optional): Suite or office number
        - residentialProperty (object, optional): Only for residential moves.
            Property details used by the Smart Fill algorithm to auto-populate rooms.
            Fields:
              - propertyType (string): One of: "single_family", "condo", "townhouse",
                  "apartment", "manufactured", "multi_family"
              - bedrooms (number): Number of bedrooms
              - bathrooms (number): Number of bathrooms
              - squareFootage (number): Total square footage
              - stories (number): Number of floors/stories
              - basement (boolean): Whether the property has a basement
              - garageSpaces (number): Number of garage parking spaces
              - pool (boolean): Whether the property has a pool
              - totalRoomCount (number): Total number of rooms
              - longCarry (boolean): Truck cannot park close to building entrance
    - internalMove (boolean): true = moving within the same building (no destination leg).
        When true, destination is ignored and destination logistics are skipped.
        Default: false
    - destination (object): Same shape as origin. Required unless internalMove = true.

  TRANSIT AUTO-CALCULATION: The system automatically handles all transit data:
    1. Geocodes each address → Google Maps Place ID
    2. Calls the Distance Matrix API to calculate origin → destination driving time
    3. Calculates warehouse → job-site travel times for all org warehouses
    4. Selects the closest warehouse for origin and destination legs
       (used for portal-to-portal travel time rate calculations)
  Do NOT include a "transit" or "placeId" field — they will be ignored.
  Use real street addresses for accurate transit calculations. If an address
  cannot be geocoded, a random transit time fallback is used.

OPTIONAL FIELDS — RESIDENTIAL SPECIFIC
---------------------------------------

numberOfBedrooms (number)
  Number of bedrooms — residential quotes/leads only. Top-level shorthand.

squareFootage (number)
  Square footage — residential quotes/leads only. Top-level shorthand.

originStories (number)
  Number of floors at origin.

destinationStories (number)
  Number of floors at destination.

originElevatorAccess (boolean)
  Whether elevator access is available at origin.

destinationElevatorAccess (boolean)
  Whether elevator access is available at destination.

originZipCode (string)
  ZIP code for the origin — useful for leads without a full address.
  Example: "10001"

destinationZipCode (string)
  ZIP code for the destination — useful for leads without a full address.
  Example: "07090"

rooms (array of objects) — RESIDENTIAL ONLY
  List of rooms to add to the residential quote. For each room, the system uses
  Smart Fill logic to automatically populate inventory items.
  Each room:
    - roomName (string): MUST match a room name in the org's residential rooms list
        (case-insensitive). If no match is found, the room is skipped.

  IMPORTANT: Any per-room inventory provided is IGNORED. Instead, the system uses
  Smart Fill to determine the appropriate inventory for each room:
    1. Evaluates Auto-Add Rules to determine which rooms to add and how many
    2. Determines the Smart Profile level (minimal, standard, heavy, full) based on
       the property's square footage and property type
    3. Populates each room with items from the matching profile level

  If rooms are omitted for a residential quote, no rooms are added (the estimator
  can use Smart Fill manually later in the app).

  NOTE: For residential moves, do NOT include casegoodsInventory or modularWorkstations.
  Room-based inventory is used instead.

OPTIONAL FIELDS — LOGISTICS
-----------------------------

originLogistics (object)
  Describes the path from the truck to the office/home at the origin site.
  Fields:
    - strides1 (number): Number of strides from the truck/entrance to the waypoint.
        Typical range: 10–200. Each stride ≈ 2.5 feet.
        IMPORTANT: At least one strides value (strides1 or strides2) must be > 0
        for the formula engine to run.
    - waypoint (string): The intermediate transport method. MUST be one of:
        "Truck"     → No intermediate stop; movers walk directly from truck to office.
        "Stairs"    → Movers use stairs (requires "stories" field).
        "Elevator"  → Movers use an elevator (adds +1 to optimum crew size).
    - stories (number): Number of stories climbed via stairs. Required when waypoint = "Stairs".
        Each story adds 9 to the optimum crew size calculation. Range: 1–10+.
    - strides2 (number): Strides from the waypoint to the final destination inside the building.
        Only used when waypoint ≠ "Truck". Typical range: 10–150.
    - loadingDock (boolean): true = truck-height loading dock available.
        When false, adds +2 to the optimum crew size. Most buildings have one, but
        vary this for realistic data.

destinationLogistics (object)
  Same shape as originLogistics. Only used when internalMove = false.
  All the same rules apply. Vary the values to create different crew size calculations
  at origin vs. destination.

OPTIONAL FIELDS — COMMERCIAL INVENTORY
----------------------------------------

casegoodsInventory (array of objects) — COMMERCIAL ONLY
  List of standard furniture items (NOT modular components) being moved.
  Each item:
    - itemName (string): MUST exactly match an inventory item name of type "Casegoods"
        in the target org's inventory list (case-insensitive). If the name doesn't match,
        the quote will be rejected during upload.
    - quantity (number): Integer ≥ 1. How many of this item are being moved.

  Only include "Casegoods"-type items here. Modular items (panels, connectors, etc.)
  go in modularWorkstations.samples instead — see below.

  NOTE: If a casegoods item has associated equipment configured in the org inventory
  (via addEquipment), the system automatically derives and adds those equipment items
  during import. You do not need to list equipment items separately.

  The KZ values are looked up automatically from the org inventory during import.
  Total KZ determines truckloads (totalKZ / 700), which determines whether the
  job is Shuttle (> 4 truckloads) or Load & Go (≤ 4 truckloads).
  For diverse data: include 2–15 line items per quote with varying quantities.

modularWorkstations (object) — COMMERCIAL ONLY
  Modular office furniture (cubicles, panel systems) that may need disassembly/reassembly.
  Fields:
    - totalWorkstations (number): Total modular workstations at the site.
    - moverDisassembling (boolean): true = movers handle disassembly (adds 5% to modular man-hours).
    - moverReassembling (boolean): true = movers handle reassembly (adds 30% to modular man-hours).
    - workstationsSampled (number): How many workstations were sampled to estimate components.
    - samples (array of objects): Modular COMPONENTS observed per sampled workstation.
        Each sample:
          - itemName (string): Must match an org inventory item of type "Modular Component"
              (case-insensitive). Rejected if no match. Do NOT include "Modular Equipment"
              items here — those are derived automatically (see below).
          - quantitySampled (number): Integer ≥ 1. How many of this component were found
              per sampled workstation.

  During import, the system automatically:
    1. Scales sampled quantities to all workstations:
       quantity = ceil(quantitySampled × totalWorkstations / workstationsSampled)
    2. Derives associated equipment quantities from each item's addEquipment entries
       (this applies to both casegoods and modular components)
    3. Adds all items — components, casegoods, and derived equipment — to the
       quote's inventory for the formula engine

  So in samples, only list "Modular Component"-type items. Equipment is always
  computed automatically from the addEquipment configuration in the org inventory.

  For diverse data: some quotes should have modular workstations, others should not.
  Vary moverDisassembling and moverReassembling to show different cost impacts.

OPTIONAL FIELDS — SERVICES & OUTCOME
--------------------------------------

scopeOfServices (array of objects)
  Defines which services are included or excluded from the quote.
  Each org has a pre-configured list of services (managed in Org Settings > Services).
  Each item:
    - serviceName (string): MUST exactly match an org service name (case-insensitive).
        If the name doesn't match any service in the org's service list, the item is
        silently skipped (the quote is still imported without it).
    - party (string): "Customer" or "Mover" — indicates who is responsible for the service.
        Must match the party configured on the org service.
    - enabled (boolean): true = this service is included in the quote, false = excluded.
        This lets you explicitly mark some services as "not included" on the quote.

  If scopeOfServices is omitted, the quote is imported with no services selected
  (the estimator can toggle them on later in the app).

  For diverse data: include scope of services on most quotes. Toggle different
  services on/off to show variety.

outcome (object, optional)
  Win/loss outcome reason — only meaningful for quotes with quoteStatus "booked" or "declined".
  Fields:
    - reason (string): The reason the quote was won or lost. For realistic data, use one of:
        Won reasons: "Price was competitive", "Relationship / trust", "Speed of response",
          "Scope of services offered", "Referral / repeat client", "Customer did not state", "Other"
        Lost reasons: "Lost on price (too expensive)", "Lost to competitor",
          "Client postponed / cancelled move", "Client went with internal team",
          "No response / went cold", "Scope mismatch", "Customer did not state", "Other"
    - note (string, optional): Free-form note about the outcome.
  If omitted for booked/declined quotes, the outcome will be empty (the dashboard's
  "Outcome Reasons" chart uses this data).

notes (string, optional)
  Free-form estimator notes attached to the quote.

externalId (string, optional)
  External CRM record ID (e.g., SmartMoving job ID). Unique per org.

source (string, optional)
  Which system created the quote (e.g., "smartmoving", "supermove", "manual").

FORMULA ENGINE NOTES
--------------------
The formula runs automatically during import if the quote has:
  1. At least one inventory item (from casegoodsInventory or extrapolated modular items)
  2. Origin logistics with at least one strides value > 0

Key formula behaviors:
  - Combined Optimum Crew = Origin Crew + Destination Crew
  - Truckloads = Total KZ / 700
  - Method: Shuttle if truckloads > 4, Load & Go otherwise
  - Shuttle jobs use more trucks (3–7) and more drivers based on transit time
  - Crew size is capped by site capacity; extra shifts added if needed
  - Final transit time: transit < 1 hr → 0; otherwise transit − 1 hr
  - Cost = Total Hourly Rate × Total Hours

TIPS FOR GENERATING DIVERSE DEMO DATA
--------------------------------------
1. Mix type: include both "commercial" and "residential" records
2. Vary company/household sizes: small (20 employees), medium (100), large (500+)
3. Mix quoteStatuses: mostly "draft" and "booked", some "sent" and "response_received",
   few "declined" and "cancelled"
4. Mix lifecycleStages: include leads, quotes, and jobs
5. For leads, vary leadStatus, inboundChannel, and discoverySource
6. Spread createdAt dates across several months for realistic timelines
7. Use all three timing options to show different shift lengths
8. Alternate between Elevator, Stairs, and Truck waypoints
9. Vary loadingDock between true/false
10. Set strides1 from 10 (close parking) to 200+ (distant parking)
11. Include some internal moves (internalMove: true) — these have no destination
12. Use real addresses in different locations/distances to get varied transit times
    (short transit → Load & Go, long transit → Shuttle outcomes)
13. Some commercial quotes with modular workstations, others without
14. Vary inventory from 2 items (small move) to 15+ items (large move)
15. Include a few quotes with minimal data (just company name + quoteStatus) to show drafts
16. Add outcome reasons to all booked and declined quotes — vary the reasons
17. Include scopeOfServices on most quotes — toggle different services on/off
18. For residential: vary property types, bedroom counts, square footage, and room lists
19. Include moveType across local, intrastate, and interstate
20. Mix bindingQuote between true and false
`;

// ── Download helpers ───────────────────────────────────────────────────────

function downloadFile(content: string, fileName: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

// ── Bulk Upload Dropdown ───────────────────────────────────────────────────

function BulkQuoteUploadButton({
  onUploadClick,
}: {
  onUploadClick: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="h-11 px-4 rounded-xl border border-stone bg-white
                   flex items-center gap-2
                   text-small font-semibold text-slate
                   hover:border-plum hover:text-plum active:bg-mist
                   transition-colors duration-150"
      >
        <Upload size={15} />
        Import Records
        <ChevronDown size={14} className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-40 min-w-[220px]
                        bg-white border border-stone rounded-xl shadow-brand-md overflow-hidden">
          <button
            type="button"
            onClick={() => { setOpen(false); onUploadClick(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-small text-midnight
                       hover:bg-mist active:bg-stone transition-colors text-left"
          >
            <Upload size={15} className="text-slate shrink-0" />
            Upload JSON File
          </button>
          <div className="h-px bg-stone mx-3" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              downloadFile(QUOTE_TEMPLATE, 'quote-template.json', 'application/json');
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-small text-midnight
                       hover:bg-mist active:bg-stone transition-colors text-left"
          >
            <Download size={15} className="text-slate shrink-0" />
            Download Template
          </button>
          <div className="h-px bg-stone mx-3" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              downloadFile(LLM_INSTRUCTIONS, 'quote-field-reference.txt', 'text/plain');
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-small text-midnight
                       hover:bg-mist active:bg-stone transition-colors text-left"
          >
            <Download size={15} className="text-slate shrink-0" />
            Download Field Reference
          </button>
        </div>
      )}
    </div>
  );
}

// ── Org Picker + Upload Modal ──────────────────────────────────────────────

function BulkUploadModal({
  onClose,
}: {
  onClose: () => void;
}) {
  const [demoOrgs, setDemoOrgs] = useState<DemoOrg[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadResult, setUploadResult] = useState<{
    imported: number;
    skipped: number;
    total: number;
    updated?: number;
    details?: string[];
  } | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ success: boolean; data: DemoOrg[] }>('/admin/demo-orgs');
        setDemoOrgs(res.data);
      } catch {
        setUploadError('Failed to load demo organizations');
      } finally {
        setLoadingOrgs(false);
      }
    })();
  }, []);

  async function handleUpload() {
    if (!file || !selectedOrgId) return;
    setUploading(true);
    setUploadError('');
    setUploadResult(null);
    try {
      const text = await readFileAsText(file);
      let quotes: unknown;
      try {
        quotes = JSON.parse(text);
      } catch {
        setUploadError('Invalid JSON file');
        return;
      }
      if (!Array.isArray(quotes)) {
        setUploadError('JSON must be an array of quote objects');
        return;
      }
      const res = await api.post<{
        success: boolean;
        data: { imported: number; skipped: number; total: number; updated?: number };
        error?: string;
        details?: string[];
      }>('/admin/bulk-quotes', { orgId: selectedOrgId, quotes }, { timeoutMs: 300_000 });
      setUploadResult({
        ...res.data,
        ...(res.details?.length ? { details: res.details } : {}),
        ...(res.data.updated ? { updated: res.data.updated } : {}),
      });
    } catch (err: unknown) {
      if (err instanceof ApiError && err.details?.length) {
        // All quotes invalid — show summary with errors
        const errData = err.data as { imported?: number; skipped?: number; total?: number } | undefined;
        setUploadResult({
          imported: errData?.imported ?? 0,
          skipped: errData?.skipped ?? errData?.total ?? 0,
          total: errData?.total ?? 0,
          details: err.details,
        });
      } else {
        const msg = err instanceof Error ? err.message : 'Upload failed';
        setUploadError(msg);
      }
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div
        className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10
                   md:rounded-2xl md:max-w-md md:w-full md:pb-6"
      >
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-h3 text-midnight">Import Records</h2>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-lg border border-stone text-slate flex items-center justify-center
                       hover:border-midnight hover:text-midnight transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {uploadResult ? (
          <div className="flex flex-col gap-3 py-4">
            {/* Status icon */}
            <div className="flex justify-center">
              {uploadResult.imported > 0 ? (
                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
                  <Check size={24} className="text-emerald-600" />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-full bg-rose/10 flex items-center justify-center">
                  <AlertCircle size={24} className="text-rose" />
                </div>
              )}
            </div>

            {/* Summary counts */}
            <div className="text-center space-y-1">
              <p className="text-small font-semibold text-midnight">
                {uploadResult.imported} of {uploadResult.total} quote{uploadResult.total !== 1 ? 's' : ''} imported
                {uploadResult.updated ? ` (${uploadResult.updated} updated)` : ''}
              </p>
              {uploadResult.skipped > 0 && (
                <p className="text-[13px] text-slate">
                  {uploadResult.skipped} skipped due to validation errors
                </p>
              )}
            </div>

            {/* Validation errors (expandable) */}
            {uploadResult.details && uploadResult.details.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50">
                <button
                  type="button"
                  onClick={() => setShowErrors(!showErrors)}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-left"
                >
                  <span className="text-[13px] font-medium text-amber-800">
                    {uploadResult.details.length} validation error{uploadResult.details.length !== 1 ? 's' : ''}
                  </span>
                  <ChevronDown
                    size={14}
                    className={`text-amber-600 transition-transform ${showErrors ? 'rotate-180' : ''}`}
                  />
                </button>
                {showErrors && (
                  <ul className="px-3 pb-2.5 space-y-1 max-h-48 overflow-y-auto">
                    {uploadResult.details.map((err, i) => (
                      <li key={i} className="text-[12px] text-amber-900 leading-snug">
                        {err}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={onClose}
              className="mt-1 h-11 px-6 rounded-xl bg-plum text-white text-small font-semibold
                         hover:bg-rose active:opacity-80 transition-colors self-center"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {/* Org selector */}
            <div className="mb-4">
              <label
                htmlFor="bulk-org-select"
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1.5"
              >
                Target Organization (Demo Only)
              </label>
              {loadingOrgs ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : demoOrgs.length === 0 ? (
                <p className="text-[13px] text-slate py-2">
                  No demo organizations found. Mark an org as &ldquo;Demo&rdquo; in the
                  organization settings first.
                </p>
              ) : (
                <SearchableDropdown
                  id="bulk-org-select"
                  options={[
                    { value: '', label: 'Select an organization…' },
                    ...demoOrgs.map(org => ({ value: org.id, label: org.name })),
                  ]}
                  value={selectedOrgId}
                  onChange={val => setSelectedOrgId(val)}
                  placeholder="Search organizations…"
                />
              )}
            </div>

            {/* File picker */}
            <div className="mb-4">
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1.5">
                JSON File
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-3 h-11 px-3 rounded-lg border border-dashed border-stone
                           bg-mist/50 cursor-pointer hover:border-plum transition-colors"
              >
                <Upload size={15} className="text-slate shrink-0" />
                <span className="text-small text-slate truncate">
                  {file ? file.name : 'Click to select a .json file'}
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={e => {
                  setFile(e.target.files?.[0] ?? null);
                  setUploadError('');
                  e.target.value = '';
                }}
              />
            </div>

            {uploadError && (
              <div className="mb-4 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose/10 border border-rose/30">
                <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
                <p className="text-[13px] text-rose whitespace-pre-wrap">{uploadError}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-11 rounded-xl border border-stone text-small text-slate font-semibold
                           hover:border-midnight hover:text-midnight transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpload}
                disabled={uploading || !selectedOrgId || !file}
                className="flex-1 h-11 rounded-xl bg-plum text-white text-small font-semibold
                           hover:bg-rose active:opacity-80 disabled:opacity-50
                           transition-colors flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Uploading…
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Upload
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Demo Quote Template ──────────────────────────────────────────────────

interface DemoTemplate {
  customerCompanyName?: string;
  numberOfEmployees?: number;
  pointOfContact?: { name?: string; phone?: string; email?: string; notes?: string };
  quoteLeadDisplayName?: string;
  moveLeadDisplayName?: string;
  movingDate?: { timing?: string };
  addresses?: {
    origin?: { address: string; placeId: string; floor?: string; officeNumber?: string };
    internalMove?: boolean;
    destination?: { address: string; placeId: string; floor?: string; officeNumber?: string };
  };
  originLogistics?: { strides1?: number; distanceFeet1?: number; waypoint?: string; stories?: number; strides2?: number; distanceFeet2?: number; loadingDock?: boolean };
  destinationLogistics?: { strides1?: number; distanceFeet1?: number; waypoint?: string; stories?: number; strides2?: number; distanceFeet2?: number; loadingDock?: boolean };
  casegoodsInventory?: { itemId: string; itemName: string; quantity: number }[];
  modularWorkstations?: { totalWorkstations: number; moverDisassembling: boolean; moverReassembling: boolean; workstationsSampled: number; samples: { itemId: string; itemName: string; quantitySampled: number }[] };
  scopeOfServices?: { serviceId: string; serviceName: string; party: 'Customer' | 'Mover'; order: number; enabled: boolean }[];
  notes?: string;
  updatedAt?: string;
  updatedBy?: string;
}

const TIMING_OPTIONS = ['Work week - day', 'Work week - evening', 'Weekend'];
const WAYPOINT_OPTIONS = ['Truck', 'Stairs', 'Elevator'];

function SectionLabel({ label }: { label: string }) {
  return (
    <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2 mt-4 first:mt-0">
      {label}
    </p>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <label className="block text-[12px] font-medium text-midnight mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls = `w-full h-10 px-3 rounded-lg border border-stone bg-white
  text-small text-midnight placeholder:text-slate/60
  focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors`;


function DemoQuoteTemplateSection() {
  const [template, setTemplate] = useState<DemoTemplate>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Master data for pickers
  const [masterItems, setMasterItems] = useState<MasterInventoryItem[]>([]);
  const [masterServices, setMasterServices] = useState<MasterServiceItem[]>([]);

  const fetchTemplate = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get<{ success: boolean; data: DemoTemplate }>('/admin/demo-quote-template');
      setTemplate(res.data ?? {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplate(); }, [fetchTemplate]);

  // Fetch master inventory + services when editing starts
  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const [invRes, svcRes] = await Promise.all([
          api.get<{ success: boolean; data: MasterInventoryItem[] }>('/admin/master-inventory'),
          api.get<{ success: boolean; data: MasterServiceItem[] }>('/admin/master-services'),
        ]);
        setMasterItems(invRes.data ?? []);
        setMasterServices(svcRes.data ?? []);
      } catch { /* non-critical */ }
    })();
  }, [editing]);

  function updateField<K extends keyof DemoTemplate>(key: K, value: DemoTemplate[K]) {
    setTemplate(prev => ({ ...prev, [key]: value }));
  }

  function updateNested<K extends keyof DemoTemplate>(
    key: K,
    field: string,
    value: unknown,
  ) {
    setTemplate(prev => ({
      ...prev,
      [key]: { ...(prev[key] as Record<string, unknown> ?? {}), [field]: value },
    }));
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await api.put<{ success: boolean; data: DemoTemplate }>('/admin/demo-quote-template', template);
      setTemplate(res.data);
      setEditing(false);
      setSaveMsg('Template saved');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveMsg(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  // ── Inventory helpers ──
  function addInventoryItem() {
    const inv = template.casegoodsInventory ?? [];
    setTemplate(prev => ({ ...prev, casegoodsInventory: [...inv, { itemId: '', itemName: '', quantity: 1 }] }));
  }

  function removeInventoryItem(idx: number) {
    const inv = [...(template.casegoodsInventory ?? [])];
    inv.splice(idx, 1);
    setTemplate(prev => ({ ...prev, casegoodsInventory: inv }));
  }

  function updateInventoryItem(idx: number, field: string, value: unknown) {
    const inv = [...(template.casegoodsInventory ?? [])];
    inv[idx] = { ...inv[idx], [field]: value };
    setTemplate(prev => ({ ...prev, casegoodsInventory: inv }));
  }

  function selectInventoryItem(idx: number, itemId: string) {
    const item = masterItems.find(i => i.id === itemId);
    if (!item) return;
    const inv = [...(template.casegoodsInventory ?? [])];
    inv[idx] = { ...inv[idx], itemId: item.id, itemName: item.name };
    setTemplate(prev => ({ ...prev, casegoodsInventory: inv }));
  }

  // ── Modular sample helpers ──
  function addModularSample() {
    const ws = template.modularWorkstations ?? { totalWorkstations: 0, moverDisassembling: false, moverReassembling: false, workstationsSampled: 0, samples: [] };
    setTemplate(prev => ({ ...prev, modularWorkstations: { ...ws, samples: [...ws.samples, { itemId: '', itemName: '', quantitySampled: 1 }] } }));
  }

  function removeModularSample(idx: number) {
    const ws = { ...(template.modularWorkstations ?? { totalWorkstations: 0, moverDisassembling: false, moverReassembling: false, workstationsSampled: 0, samples: [] }) };
    ws.samples = [...ws.samples];
    ws.samples.splice(idx, 1);
    setTemplate(prev => ({ ...prev, modularWorkstations: ws }));
  }

  function selectModularItem(idx: number, itemId: string) {
    const item = masterItems.find(i => i.id === itemId);
    if (!item) return;
    const ws = { ...(template.modularWorkstations ?? { totalWorkstations: 0, moverDisassembling: false, moverReassembling: false, workstationsSampled: 0, samples: [] }) };
    ws.samples = [...ws.samples];
    ws.samples[idx] = { ...ws.samples[idx], itemId: item.id, itemName: item.name };
    setTemplate(prev => ({ ...prev, modularWorkstations: ws }));
  }

  // ── Services helpers ──
  function addService() {
    const svc = template.scopeOfServices ?? [];
    setTemplate(prev => ({ ...prev, scopeOfServices: [...svc, { serviceId: '', serviceName: '', party: 'Mover' as const, order: svc.length, enabled: true }] }));
  }

  function removeService(idx: number) {
    const svc = [...(template.scopeOfServices ?? [])];
    svc.splice(idx, 1);
    setTemplate(prev => ({ ...prev, scopeOfServices: svc }));
  }

  function selectService(idx: number, serviceId: string) {
    const svc = masterServices.find(s => s.id === serviceId);
    if (!svc) return;
    const list = [...(template.scopeOfServices ?? [])];
    list[idx] = { ...list[idx], serviceId: svc.id, serviceName: svc.name, party: svc.party };
    setTemplate(prev => ({ ...prev, scopeOfServices: list }));
  }

  const casegoodsItems = masterItems.filter(i => i.type === 'Casegoods');
  const modularItems = masterItems.filter(i => i.type === 'Modular Component');

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm">
      <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList size={16} className="text-plum" />
          <p className="font-semibold text-small text-midnight">Demo Quote Template</p>
        </div>
        <div className="flex items-center gap-2">
          {saveMsg && (
            <span className={`text-xs font-medium ${saveMsg.includes('Failed') ? 'text-rose' : 'text-emerald-600'}`}>
              {saveMsg}
            </span>
          )}
          {!editing ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPreviewOpen(p => !p)}
                className="h-8 px-3 rounded-lg border border-stone text-[12px] font-semibold text-slate
                           flex items-center gap-1.5 hover:border-plum hover:text-plum transition-colors"
              >
                <Eye size={12} />
                {previewOpen ? 'Hide' : 'Preview'}
              </button>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="h-8 px-3 rounded-lg border border-stone text-[12px] font-semibold text-slate
                           flex items-center gap-1.5 hover:border-plum hover:text-plum transition-colors"
              >
                <Pencil size={12} />
                Edit
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button type="button" onClick={() => { setEditing(false); fetchTemplate(); }}
                className="h-8 px-3 rounded-lg border border-stone text-[12px] font-semibold text-slate
                           flex items-center gap-1.5 hover:border-midnight hover:text-midnight transition-colors">
                Cancel
              </button>
              <button type="button" onClick={handleSave} disabled={saving}
                className="h-8 px-3 rounded-lg bg-plum text-white text-[12px] font-semibold
                           flex items-center gap-1.5 hover:bg-rose disabled:opacity-50 transition-colors">
                <Save size={12} />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><LoadingSpinner size="sm" /></div>
      ) : error ? (
        <div className="px-4 py-4 text-sm text-rose">{error}</div>
      ) : !editing && !previewOpen ? (
        <div className="px-4 py-4">
          <p className="text-[13px] text-slate leading-snug">
            Configure the demo quote template used during Estimator onboarding. This template
            is cloned into a real quote when a new Estimator starts the guided tour.
          </p>
          {template.updatedAt && (
            <p className="text-[11px] text-slate/60 mt-2">
              Last updated: {new Date(template.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>
      ) : !editing && previewOpen ? (
        /* ── Preview (read-only) ── */
        <div className="px-4 py-4 space-y-3">
          {template.customerCompanyName && (
            <div><span className="text-[11px] font-bold uppercase text-slate">Company:</span> <span className="text-sm text-midnight">{template.customerCompanyName}</span></div>
          )}
          {template.numberOfEmployees && (
            <div><span className="text-[11px] font-bold uppercase text-slate">Employees:</span> <span className="text-sm text-midnight">{template.numberOfEmployees}</span></div>
          )}
          {template.pointOfContact?.name && (
            <div><span className="text-[11px] font-bold uppercase text-slate">Contact:</span> <span className="text-sm text-midnight">{template.pointOfContact.name}{template.pointOfContact.phone ? ` — ${template.pointOfContact.phone}` : ''}{template.pointOfContact.email ? ` — ${template.pointOfContact.email}` : ''}</span></div>
          )}
          {template.quoteLeadDisplayName && (
            <div><span className="text-[11px] font-bold uppercase text-slate">Quote Lead:</span> <span className="text-sm text-midnight">{template.quoteLeadDisplayName}</span></div>
          )}
          {template.moveLeadDisplayName && (
            <div><span className="text-[11px] font-bold uppercase text-slate">Move Lead:</span> <span className="text-sm text-midnight">{template.moveLeadDisplayName}</span></div>
          )}
          {template.movingDate?.timing && (
            <div><span className="text-[11px] font-bold uppercase text-slate">Timing:</span> <span className="text-sm text-midnight">{template.movingDate.timing}</span></div>
          )}
          {template.addresses?.origin?.address && (
            <div><span className="text-[11px] font-bold uppercase text-slate">Origin:</span> <span className="text-sm text-midnight">{template.addresses.origin.address}{template.addresses.origin.floor ? `, Floor ${template.addresses.origin.floor}` : ''}</span></div>
          )}
          {template.addresses?.destination?.address && (
            <div><span className="text-[11px] font-bold uppercase text-slate">Destination:</span> <span className="text-sm text-midnight">{template.addresses.destination.address}{template.addresses.destination.floor ? `, Floor ${template.addresses.destination.floor}` : ''}</span></div>
          )}
          {(template.casegoodsInventory ?? []).length > 0 && (
            <div>
              <span className="text-[11px] font-bold uppercase text-slate">Inventory ({template.casegoodsInventory!.length} items):</span>
              <ul className="mt-1 space-y-0.5">
                {template.casegoodsInventory!.map((item, i) => (
                  <li key={i} className="text-sm text-midnight">{item.itemName || '(unnamed)'} × {item.quantity}</li>
                ))}
              </ul>
            </div>
          )}
          {(template.scopeOfServices ?? []).length > 0 && (
            <div>
              <span className="text-[11px] font-bold uppercase text-slate">Services ({template.scopeOfServices!.length}):</span>
              <ul className="mt-1 space-y-0.5">
                {template.scopeOfServices!.map((svc, i) => (
                  <li key={i} className="text-sm text-midnight">
                    {svc.serviceName || '(unnamed)'} — {svc.party} {svc.enabled ? '' : '(disabled)'}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {template.notes && (
            <div><span className="text-[11px] font-bold uppercase text-slate">Notes:</span> <span className="text-sm text-midnight whitespace-pre-wrap">{template.notes}</span></div>
          )}
          {!template.customerCompanyName && !template.notes && (template.casegoodsInventory ?? []).length === 0 && (
            <p className="text-[13px] text-slate italic">No template configured yet. Click Edit to set up.</p>
          )}
        </div>
      ) : (
        /* ── Edit form ── */
        <div className="px-4 py-4">
          {/* Customer Details */}
          <SectionLabel label="Customer Details" />
          <FieldRow label="Company Name">
            <input type="text" value={template.customerCompanyName ?? ''} onChange={e => updateField('customerCompanyName', e.target.value)} placeholder="e.g. Acme Corp" className={inputCls} />
          </FieldRow>
          <FieldRow label="Number of Employees">
            <input type="number" value={template.numberOfEmployees ?? ''} onChange={e => updateField('numberOfEmployees', e.target.value ? Number(e.target.value) : undefined)} placeholder="e.g. 120" className={inputCls} />
          </FieldRow>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Contact Name">
              <input type="text" value={template.pointOfContact?.name ?? ''} onChange={e => updateNested('pointOfContact', 'name', e.target.value)} placeholder="Jane Smith" className={inputCls} />
            </FieldRow>
            <FieldRow label="Contact Phone">
              <input type="text" value={template.pointOfContact?.phone ?? ''} onChange={e => updateNested('pointOfContact', 'phone', e.target.value)} placeholder="555-123-4567" className={inputCls} />
            </FieldRow>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Contact Email">
              <input type="email" value={template.pointOfContact?.email ?? ''} onChange={e => updateNested('pointOfContact', 'email', e.target.value)} placeholder="jane@acme.com" className={inputCls} />
            </FieldRow>
            <FieldRow label="Contact Notes">
              <input type="text" value={template.pointOfContact?.notes ?? ''} onChange={e => updateNested('pointOfContact', 'notes', e.target.value)} placeholder="Prefers morning calls" className={inputCls} />
            </FieldRow>
          </div>

          {/* Lead Names */}
          <SectionLabel label="Lead Names" />
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Quote Lead">
              <input type="text" value={template.quoteLeadDisplayName ?? ''} onChange={e => updateField('quoteLeadDisplayName', e.target.value)} placeholder="Jane Smith" className={inputCls} />
            </FieldRow>
            <FieldRow label="Move Lead">
              <input type="text" value={template.moveLeadDisplayName ?? ''} onChange={e => updateField('moveLeadDisplayName', e.target.value)} placeholder="Mike Johnson" className={inputCls} />
            </FieldRow>
          </div>

          {/* Dates */}
          <SectionLabel label="Move Timing" />
          <FieldRow label="Timing Preference">
            <CustomDropdown
              options={[
                { value: '', label: 'Select timing…' },
                ...TIMING_OPTIONS.map(t => ({ value: t, label: t })),
              ]}
              value={template.movingDate?.timing ?? ''}
              onChange={val => updateField('movingDate', { ...template.movingDate, timing: val || undefined })}
              placeholder="Select timing…"
            />
          </FieldRow>

          {/* Addresses */}
          <SectionLabel label="Addresses" />
          <FieldRow label="Origin Address">
            <input type="text" value={template.addresses?.origin?.address ?? ''} onChange={e => {
              const origin = { ...(template.addresses?.origin ?? { address: '', placeId: '' }), address: e.target.value };
              updateField('addresses', { ...template.addresses, origin });
            }} placeholder="123 Main St, Suite 400, New York, NY" className={inputCls} />
          </FieldRow>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Origin Floor">
              <input type="text" value={template.addresses?.origin?.floor ?? ''} onChange={e => {
                const origin = { ...(template.addresses?.origin ?? { address: '', placeId: '' }), floor: e.target.value };
                updateField('addresses', { ...template.addresses, origin });
              }} placeholder="e.g. 4" className={inputCls} />
            </FieldRow>
            <FieldRow label="Origin Office #">
              <input type="text" value={template.addresses?.origin?.officeNumber ?? ''} onChange={e => {
                const origin = { ...(template.addresses?.origin ?? { address: '', placeId: '' }), officeNumber: e.target.value };
                updateField('addresses', { ...template.addresses, origin });
              }} placeholder="e.g. 400" className={inputCls} />
            </FieldRow>
          </div>
          <div className="mb-3">
            <label className="flex items-center gap-2 text-[12px] font-medium text-midnight">
              <input type="checkbox" checked={template.addresses?.internalMove ?? false} onChange={e => updateField('addresses', { ...template.addresses, internalMove: e.target.checked })}
                className="w-4 h-4 rounded border-stone text-plum focus:ring-plum/30" />
              Internal Move (same building)
            </label>
          </div>
          {!template.addresses?.internalMove && (
            <>
              <FieldRow label="Destination Address">
                <input type="text" value={template.addresses?.destination?.address ?? ''} onChange={e => {
                  const destination = { ...(template.addresses?.destination ?? { address: '', placeId: '' }), address: e.target.value };
                  updateField('addresses', { ...template.addresses, destination });
                }} placeholder="456 Broadway, Floor 12, New York, NY" className={inputCls} />
              </FieldRow>
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="Destination Floor">
                  <input type="text" value={template.addresses?.destination?.floor ?? ''} onChange={e => {
                    const destination = { ...(template.addresses?.destination ?? { address: '', placeId: '' }), floor: e.target.value };
                    updateField('addresses', { ...template.addresses, destination });
                  }} placeholder="e.g. 12" className={inputCls} />
                </FieldRow>
                <FieldRow label="Destination Office #">
                  <input type="text" value={template.addresses?.destination?.officeNumber ?? ''} onChange={e => {
                    const destination = { ...(template.addresses?.destination ?? { address: '', placeId: '' }), officeNumber: e.target.value };
                    updateField('addresses', { ...template.addresses, destination });
                  }} placeholder="e.g. 1200" className={inputCls} />
                </FieldRow>
              </div>
            </>
          )}

          {/* Origin Logistics */}
          <SectionLabel label="Origin Logistics" />
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Strides (to waypoint)">
              <input type="number" value={template.originLogistics?.strides1 ?? ''} onChange={e => updateNested('originLogistics', 'strides1', e.target.value ? Number(e.target.value) : undefined)} placeholder="e.g. 50" className={inputCls} />
            </FieldRow>
            <FieldRow label="Waypoint">
              <CustomDropdown
                options={[
                  { value: '', label: 'Select…' },
                  ...WAYPOINT_OPTIONS.map(w => ({ value: w, label: w })),
                ]}
                value={template.originLogistics?.waypoint ?? ''}
                onChange={val => updateNested('originLogistics', 'waypoint', val || undefined)}
                placeholder="Select…"
              />
            </FieldRow>
          </div>
          {template.originLogistics?.waypoint === 'Stairs' && (
            <FieldRow label="Stories">
              <input type="number" value={template.originLogistics?.stories ?? ''} onChange={e => updateNested('originLogistics', 'stories', e.target.value ? Number(e.target.value) : undefined)} placeholder="e.g. 3" className={inputCls} />
            </FieldRow>
          )}
          {template.originLogistics?.waypoint && template.originLogistics.waypoint !== 'Truck' && (
            <FieldRow label="Strides (from waypoint)">
              <input type="number" value={template.originLogistics?.strides2 ?? ''} onChange={e => updateNested('originLogistics', 'strides2', e.target.value ? Number(e.target.value) : undefined)} placeholder="e.g. 30" className={inputCls} />
            </FieldRow>
          )}
          <div className="mb-3">
            <label className="flex items-center gap-2 text-[12px] font-medium text-midnight">
              <input type="checkbox" checked={template.originLogistics?.loadingDock ?? false} onChange={e => updateNested('originLogistics', 'loadingDock', e.target.checked)}
                className="w-4 h-4 rounded border-stone text-plum focus:ring-plum/30" />
              Loading Dock Available
            </label>
          </div>

          {/* Destination Logistics */}
          {!template.addresses?.internalMove && (
            <>
              <SectionLabel label="Destination Logistics" />
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="Strides (to waypoint)">
                  <input type="number" value={template.destinationLogistics?.strides1 ?? ''} onChange={e => updateNested('destinationLogistics', 'strides1', e.target.value ? Number(e.target.value) : undefined)} placeholder="e.g. 40" className={inputCls} />
                </FieldRow>
                <FieldRow label="Waypoint">
                  <CustomDropdown
                    options={[
                      { value: '', label: 'Select…' },
                      ...WAYPOINT_OPTIONS.map(w => ({ value: w, label: w })),
                    ]}
                    value={template.destinationLogistics?.waypoint ?? ''}
                    onChange={val => updateNested('destinationLogistics', 'waypoint', val || undefined)}
                    placeholder="Select…"
                  />
                </FieldRow>
              </div>
              {template.destinationLogistics?.waypoint === 'Stairs' && (
                <FieldRow label="Stories">
                  <input type="number" value={template.destinationLogistics?.stories ?? ''} onChange={e => updateNested('destinationLogistics', 'stories', e.target.value ? Number(e.target.value) : undefined)} placeholder="e.g. 2" className={inputCls} />
                </FieldRow>
              )}
              {template.destinationLogistics?.waypoint && template.destinationLogistics.waypoint !== 'Truck' && (
                <FieldRow label="Strides (from waypoint)">
                  <input type="number" value={template.destinationLogistics?.strides2 ?? ''} onChange={e => updateNested('destinationLogistics', 'strides2', e.target.value ? Number(e.target.value) : undefined)} placeholder="e.g. 25" className={inputCls} />
                </FieldRow>
              )}
              <div className="mb-3">
                <label className="flex items-center gap-2 text-[12px] font-medium text-midnight">
                  <input type="checkbox" checked={template.destinationLogistics?.loadingDock ?? false} onChange={e => updateNested('destinationLogistics', 'loadingDock', e.target.checked)}
                    className="w-4 h-4 rounded border-stone text-plum focus:ring-plum/30" />
                  Loading Dock Available
                </label>
              </div>
            </>
          )}

          {/* Casegoods Inventory */}
          <SectionLabel label="Casegoods Inventory" />
          {(template.casegoodsInventory ?? []).map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <SearchableDropdown
                options={[
                  { value: '', label: 'Select item…' },
                  ...casegoodsItems.map(ci => ({ value: ci.id, label: ci.name })),
                ]}
                value={item.itemId}
                onChange={val => selectInventoryItem(idx, val)}
                placeholder="Search items…"
              />
              <input type="number" value={item.quantity} min={1}
                onChange={e => updateInventoryItem(idx, 'quantity', Math.max(1, Number(e.target.value)))}
                className="w-20 h-10 px-2 rounded-lg border border-stone bg-white text-small text-midnight text-center
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20" />
              <button type="button" onClick={() => removeInventoryItem(idx)}
                className="w-8 h-8 rounded-lg text-slate hover:text-rose hover:bg-rose/10 flex items-center justify-center transition-colors">
                <Minus size={14} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addInventoryItem}
            className="flex items-center gap-1.5 text-[12px] font-semibold text-plum hover:text-rose transition-colors mt-1">
            <Plus size={13} /> Add Item
          </button>

          {/* Modular Workstations */}
          <SectionLabel label="Modular Workstations" />
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Total Workstations">
              <input type="number" value={template.modularWorkstations?.totalWorkstations ?? ''} min={0}
                onChange={e => {
                  const ws = template.modularWorkstations ?? { totalWorkstations: 0, moverDisassembling: false, moverReassembling: false, workstationsSampled: 0, samples: [] };
                  updateField('modularWorkstations', { ...ws, totalWorkstations: Number(e.target.value) || 0 });
                }} className={inputCls} />
            </FieldRow>
            <FieldRow label="Workstations Sampled">
              <input type="number" value={template.modularWorkstations?.workstationsSampled ?? ''} min={0}
                onChange={e => {
                  const ws = template.modularWorkstations ?? { totalWorkstations: 0, moverDisassembling: false, moverReassembling: false, workstationsSampled: 0, samples: [] };
                  updateField('modularWorkstations', { ...ws, workstationsSampled: Number(e.target.value) || 0 });
                }} className={inputCls} />
            </FieldRow>
          </div>
          <div className="flex gap-4 mb-3">
            <label className="flex items-center gap-2 text-[12px] font-medium text-midnight">
              <input type="checkbox" checked={template.modularWorkstations?.moverDisassembling ?? false}
                onChange={e => {
                  const ws = template.modularWorkstations ?? { totalWorkstations: 0, moverDisassembling: false, moverReassembling: false, workstationsSampled: 0, samples: [] };
                  updateField('modularWorkstations', { ...ws, moverDisassembling: e.target.checked });
                }} className="w-4 h-4 rounded border-stone text-plum focus:ring-plum/30" />
              Mover Disassembling
            </label>
            <label className="flex items-center gap-2 text-[12px] font-medium text-midnight">
              <input type="checkbox" checked={template.modularWorkstations?.moverReassembling ?? false}
                onChange={e => {
                  const ws = template.modularWorkstations ?? { totalWorkstations: 0, moverDisassembling: false, moverReassembling: false, workstationsSampled: 0, samples: [] };
                  updateField('modularWorkstations', { ...ws, moverReassembling: e.target.checked });
                }} className="w-4 h-4 rounded border-stone text-plum focus:ring-plum/30" />
              Mover Reassembling
            </label>
          </div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">Samples (Modular Components)</p>
          {(template.modularWorkstations?.samples ?? []).map((sample, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <SearchableDropdown
                options={[
                  { value: '', label: 'Select component…' },
                  ...modularItems.map(ci => ({ value: ci.id, label: ci.name })),
                ]}
                value={sample.itemId}
                onChange={val => selectModularItem(idx, val)}
                placeholder="Search components…"
              />
              <input type="number" value={sample.quantitySampled} min={1}
                onChange={e => {
                  const ws = { ...(template.modularWorkstations ?? { totalWorkstations: 0, moverDisassembling: false, moverReassembling: false, workstationsSampled: 0, samples: [] }) };
                  ws.samples = [...ws.samples];
                  ws.samples[idx] = { ...ws.samples[idx], quantitySampled: Math.max(1, Number(e.target.value)) };
                  updateField('modularWorkstations', ws);
                }}
                className="w-20 h-10 px-2 rounded-lg border border-stone bg-white text-small text-midnight text-center
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20" />
              <button type="button" onClick={() => removeModularSample(idx)}
                className="w-8 h-8 rounded-lg text-slate hover:text-rose hover:bg-rose/10 flex items-center justify-center transition-colors">
                <Minus size={14} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addModularSample}
            className="flex items-center gap-1.5 text-[12px] font-semibold text-plum hover:text-rose transition-colors mt-1">
            <Plus size={13} /> Add Sample
          </button>

          {/* Scope of Services */}
          <SectionLabel label="Scope of Services" />
          {(template.scopeOfServices ?? []).map((svc, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-2">
              <SearchableDropdown
                options={[
                  { value: '', label: 'Select service…' },
                  ...masterServices.map(ms => ({ value: ms.id, label: `${ms.name} (${ms.party})` })),
                ]}
                value={svc.serviceId}
                onChange={val => selectService(idx, val)}
                placeholder="Search services…"
              />
              <label className="flex items-center gap-1.5 text-[11px] font-medium text-midnight shrink-0">
                <input type="checkbox" checked={svc.enabled}
                  onChange={e => {
                    const list = [...(template.scopeOfServices ?? [])];
                    list[idx] = { ...list[idx], enabled: e.target.checked };
                    updateField('scopeOfServices', list);
                  }} className="w-4 h-4 rounded border-stone text-plum focus:ring-plum/30" />
                On
              </label>
              <button type="button" onClick={() => removeService(idx)}
                className="w-8 h-8 rounded-lg text-slate hover:text-rose hover:bg-rose/10 flex items-center justify-center transition-colors">
                <Minus size={14} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addService}
            className="flex items-center gap-1.5 text-[12px] font-semibold text-plum hover:text-rose transition-colors mt-1">
            <Plus size={13} /> Add Service
          </button>

          {/* Notes */}
          <SectionLabel label="Notes" />
          <textarea value={template.notes ?? ''} onChange={e => updateField('notes', e.target.value)}
            placeholder="Sample estimator notes…" rows={3}
            className="w-full px-3 py-2 rounded-lg border border-stone bg-white text-small text-midnight
                       placeholder:text-slate/60 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors resize-y" />
        </div>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export function DeveloperToolsPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);

  // Edit state
  const [templateId, setTemplateId] = useState('');
  const [resendFromEmail, setResendFromEmail] = useState('');
  const [resendEmailVerificationTemplateId, setResendEmailVerificationTemplateId] = useState('');
  const [resendPasswordResetTemplateId, setResendPasswordResetTemplateId] = useState('');
  const [resendEmailChangeTemplateId, setResendEmailChangeTemplateId] = useState('');
  const [resendInAppNotificationTemplateId, setResendInAppNotificationTemplateId] = useState('');
  const [resendUserInvitedTemplateId, setResendUserInvitedTemplateId] = useState('');
  const [resendQuoteEmailTemplateId, setResendQuoteEmailTemplateId] = useState('');
  const [resendPaymentRequestTemplateId, setResendPaymentRequestTemplateId] = useState('');
  const [resendPlanConfirmationTemplateId, setResendPlanConfirmationTemplateId] = useState('');
  const [resendPlanCancellationTemplateId, setResendPlanCancellationTemplateId] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ success: boolean; data: AppConfig }>('/admin/app-config');
        setConfig(res.data);
        setTemplateId(res.data.craftMyPdfTemplateId ?? '');
        setResendFromEmail(res.data.resendFromEmail ?? '');
        setResendEmailVerificationTemplateId(res.data.resendEmailVerificationTemplateId ?? '');
        setResendPasswordResetTemplateId(res.data.resendPasswordResetTemplateId ?? '');
        setResendEmailChangeTemplateId(res.data.resendEmailChangeTemplateId ?? '');
        setResendInAppNotificationTemplateId(res.data.resendInAppNotificationTemplateId ?? '');
        setResendUserInvitedTemplateId(res.data.resendUserInvitedTemplateId ?? '');
        setResendQuoteEmailTemplateId(res.data.resendQuoteEmailTemplateId ?? '');
        setResendPaymentRequestTemplateId(res.data.resendPaymentRequestTemplateId ?? '');
        setResendPlanConfirmationTemplateId(res.data.resendPlanConfirmationTemplateId ?? '');
        setResendPlanCancellationTemplateId(res.data.resendPlanCancellationTemplateId ?? '');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load config');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function handleEditClick() {
    setShowWarning(true);
  }

  function handleConfirmEdit() {
    setShowWarning(false);
    setEditing(true);
    setTemplateId(config?.craftMyPdfTemplateId ?? '');
    setResendFromEmail(config?.resendFromEmail ?? '');
    setResendEmailVerificationTemplateId(config?.resendEmailVerificationTemplateId ?? '');
    setResendPasswordResetTemplateId(config?.resendPasswordResetTemplateId ?? '');
    setResendEmailChangeTemplateId(config?.resendEmailChangeTemplateId ?? '');
    setResendInAppNotificationTemplateId(config?.resendInAppNotificationTemplateId ?? '');
    setResendUserInvitedTemplateId(config?.resendUserInvitedTemplateId ?? '');
    setResendQuoteEmailTemplateId(config?.resendQuoteEmailTemplateId ?? '');
    setResendPaymentRequestTemplateId(config?.resendPaymentRequestTemplateId ?? '');
    setResendPlanConfirmationTemplateId(config?.resendPlanConfirmationTemplateId ?? '');
    setResendPlanCancellationTemplateId(config?.resendPlanCancellationTemplateId ?? '');
  }

  function handleCancel() {
    setEditing(false);
    setSaveError('');
    setTemplateId(config?.craftMyPdfTemplateId ?? '');
    setResendFromEmail(config?.resendFromEmail ?? '');
    setResendEmailVerificationTemplateId(config?.resendEmailVerificationTemplateId ?? '');
    setResendPasswordResetTemplateId(config?.resendPasswordResetTemplateId ?? '');
    setResendEmailChangeTemplateId(config?.resendEmailChangeTemplateId ?? '');
    setResendInAppNotificationTemplateId(config?.resendInAppNotificationTemplateId ?? '');
    setResendUserInvitedTemplateId(config?.resendUserInvitedTemplateId ?? '');
    setResendQuoteEmailTemplateId(config?.resendQuoteEmailTemplateId ?? '');
    setResendPaymentRequestTemplateId(config?.resendPaymentRequestTemplateId ?? '');
    setResendPlanConfirmationTemplateId(config?.resendPlanConfirmationTemplateId ?? '');
    setResendPlanCancellationTemplateId(config?.resendPlanCancellationTemplateId ?? '');
  }

  async function handleSave() {
    setSaving(true);
    setSaveError('');
    try {
      const res = await api.patch<{ success: boolean; data: AppConfig }>('/admin/app-config', {
        craftMyPdfTemplateId: templateId.trim(),
        resendFromEmail: resendFromEmail.trim(),
        resendEmailVerificationTemplateId: resendEmailVerificationTemplateId.trim(),
        resendPasswordResetTemplateId: resendPasswordResetTemplateId.trim(),
        resendEmailChangeTemplateId: resendEmailChangeTemplateId.trim(),
        resendInAppNotificationTemplateId: resendInAppNotificationTemplateId.trim(),
        resendUserInvitedTemplateId: resendUserInvitedTemplateId.trim(),
        resendQuoteEmailTemplateId: resendQuoteEmailTemplateId.trim(),
        resendPaymentRequestTemplateId: resendPaymentRequestTemplateId.trim(),
        resendPlanConfirmationTemplateId: resendPlanConfirmationTemplateId.trim(),
        resendPlanCancellationTemplateId: resendPlanCancellationTemplateId.trim(),
      });
      setConfig(res.data);
      setEditing(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="px-4 pt-6 pb-12 md:px-8 md:max-w-2xl md:mx-auto">
      <button
        type="button"
        onClick={() => navigate('/admin/platform-tools')}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        Platform Admin Tools
      </button>

      <div className="flex items-center justify-between gap-3 mb-1">
        <h1 className="font-display text-h1 text-midnight">Developer Tools</h1>
        {!editing && !loading && config && (
          <button
            type="button"
            onClick={handleEditClick}
            className="shrink-0 h-9 px-3 rounded-lg border border-stone text-slate text-[13px]
                       font-semibold flex items-center gap-1.5
                       hover:border-plum hover:text-plum active:bg-mist
                       transition-colors duration-150"
          >
            <Pencil size={14} />
            Edit
          </button>
        )}
        {editing && (
          <button
            type="button"
            onClick={handleCancel}
            aria-label="Cancel edit"
            className="shrink-0 h-9 w-9 rounded-lg border border-stone text-slate flex items-center justify-center
                       hover:border-midnight hover:text-midnight active:bg-mist
                       transition-colors duration-150"
          >
            <X size={16} />
          </button>
        )}
      </div>
      <p className="text-[13px] text-slate mb-6 leading-snug">
        Configure how the IOMI Pro app integrates with third-party tools. These
        settings affect the entire platform &mdash; do not edit unless you are sure
        of what you are doing.
      </p>

      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load config</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && config && !editing && (
        <div className="flex flex-col gap-4">
          <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
            <div className="px-4 py-3.5 border-b border-stone">
              <p className="font-semibold text-small text-midnight">CraftMyPDF</p>
            </div>
            <div className="px-4">
              <div className="flex items-center justify-between py-3">
                <span className="text-[13px] text-slate">Quote Template ID</span>
                <span className="text-[13px] font-semibold text-midnight font-mono">
                  {config.craftMyPdfTemplateId || '—'}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
            <div className="px-4 py-3.5 border-b border-stone">
              <p className="font-semibold text-small text-midnight">Resend (Email)</p>
            </div>
            <div className="px-4">
              <div className="flex items-center justify-between py-3 border-b border-stone/40">
                <span className="text-[13px] text-slate">From Email</span>
                <span className="text-[13px] font-semibold text-midnight font-mono">
                  {config.resendFromEmail || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-stone/40">
                <span className="text-[13px] text-slate">Email Address Verification</span>
                <span className="text-[13px] font-semibold text-midnight font-mono">
                  {config.resendEmailVerificationTemplateId || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-stone/40">
                <span className="text-[13px] text-slate">Password Reset</span>
                <span className="text-[13px] font-semibold text-midnight font-mono">
                  {config.resendPasswordResetTemplateId || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-stone/40">
                <span className="text-[13px] text-slate">Email Address Change</span>
                <span className="text-[13px] font-semibold text-midnight font-mono">
                  {config.resendEmailChangeTemplateId || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-stone/40">
                <span className="text-[13px] text-slate">In-App Notification</span>
                <span className="text-[13px] font-semibold text-midnight font-mono">
                  {config.resendInAppNotificationTemplateId || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-stone/40">
                <span className="text-[13px] text-slate">User Invited</span>
                <span className="text-[13px] font-semibold text-midnight font-mono">
                  {config.resendUserInvitedTemplateId || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-stone/40">
                <span className="text-[13px] text-slate">Quote Email</span>
                <span className="text-[13px] font-semibold text-midnight font-mono">
                  {config.resendQuoteEmailTemplateId || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-stone/40">
                <span className="text-[13px] text-slate">Payment Request</span>
                <span className="text-[13px] font-semibold text-midnight font-mono">
                  {config.resendPaymentRequestTemplateId || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-stone/40">
                <span className="text-[13px] text-slate">Plan Confirmation</span>
                <span className="text-[13px] font-semibold text-midnight font-mono">
                  {config.resendPlanConfirmationTemplateId || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-[13px] text-slate">Plan Cancellation</span>
                <span className="text-[13px] font-semibold text-midnight font-mono">
                  {config.resendPlanCancellationTemplateId || '—'}
                </span>
              </div>
            </div>
          </div>

          {/* ── Import Leads, Quotes, and Jobs ───────────────────────────── */}
          <div className="bg-white border border-stone rounded-xl shadow-brand-sm">
            <div className="px-4 py-3.5 border-b border-stone">
              <p className="font-semibold text-small text-midnight">Import Leads, Quotes, and Jobs</p>
            </div>
            <div className="px-4 py-4">
              <p className="text-[13px] text-slate leading-snug mb-4">
                Import leads, quotes, and jobs into a demo organization from a JSON file.
                Download the template for the expected format, or the field reference
                for detailed instructions on every field.
              </p>
              <BulkQuoteUploadButton onUploadClick={() => setShowBulkModal(true)} />
            </div>
          </div>

          {/* ── Demo Quote Template ──────────────────────────────────── */}
          <DemoQuoteTemplateSection />
        </div>
      )}

      {!loading && !error && config && editing && (
        <div className="flex flex-col gap-5">
          {saveError && (
            <div className="px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
              {saveError}
            </div>
          )}

          <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
            <div className="px-4 py-3.5 border-b border-stone">
              <p className="font-semibold text-small text-midnight">CraftMyPDF</p>
            </div>
            <div className="px-4 py-4">
              <label
                htmlFor="craftmypdf-template-id"
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
              >
                Quote Template ID
              </label>
              <input
                id="craftmypdf-template-id"
                type="text"
                value={templateId}
                onChange={e => setTemplateId(e.target.value)}
                placeholder="e.g. a1b2c3d4e5f6..."
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight font-mono placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
            </div>
          </div>

          <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
            <div className="px-4 py-3.5 border-b border-stone">
              <p className="font-semibold text-small text-midnight">Resend (Email)</p>
            </div>
            <div className="px-4 py-4">
              <label
                htmlFor="resend-from-email"
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
              >
                From Email
              </label>
              <input
                id="resend-from-email"
                type="text"
                value={resendFromEmail}
                onChange={e => setResendFromEmail(e.target.value)}
                placeholder='e.g. No-reply <noreply@yourdomain.com>'
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight font-mono placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <label
                htmlFor="resend-email-verification"
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1 mt-4"
              >
                Email Address Verification
              </label>
              <input
                id="resend-email-verification"
                type="text"
                value={resendEmailVerificationTemplateId}
                onChange={e => setResendEmailVerificationTemplateId(e.target.value)}
                placeholder="Resend Template ID — e.g. email-verification"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight font-mono placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <label
                htmlFor="resend-password-reset"
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1 mt-4"
              >
                Password Reset
              </label>
              <input
                id="resend-password-reset"
                type="text"
                value={resendPasswordResetTemplateId}
                onChange={e => setResendPasswordResetTemplateId(e.target.value)}
                placeholder="Resend Template ID — e.g. password-reset"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight font-mono placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <label
                htmlFor="resend-email-change"
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1 mt-4"
              >
                Email Address Change
              </label>
              <input
                id="resend-email-change"
                type="text"
                value={resendEmailChangeTemplateId}
                onChange={e => setResendEmailChangeTemplateId(e.target.value)}
                placeholder="Resend Template ID — e.g. email-change"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight font-mono placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <label
                htmlFor="resend-in-app-notification"
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1 mt-4"
              >
                In-App Notification
              </label>
              <input
                id="resend-in-app-notification"
                type="text"
                value={resendInAppNotificationTemplateId}
                onChange={e => setResendInAppNotificationTemplateId(e.target.value)}
                placeholder="Resend Template ID — e.g. in-app-notification"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight font-mono placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <label
                htmlFor="resend-user-invited"
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1 mt-4"
              >
                User Invited
              </label>
              <input
                id="resend-user-invited"
                type="text"
                value={resendUserInvitedTemplateId}
                onChange={e => setResendUserInvitedTemplateId(e.target.value)}
                placeholder="Resend Template ID — e.g. user-invited"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight font-mono placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <label
                htmlFor="resend-quote-email"
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1 mt-4"
              >
                Quote Email
              </label>
              <input
                id="resend-quote-email"
                type="text"
                value={resendQuoteEmailTemplateId}
                onChange={e => setResendQuoteEmailTemplateId(e.target.value)}
                placeholder="Resend Template ID — e.g. quote-email"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight font-mono placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <label
                htmlFor="resend-payment-request"
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1 mt-4"
              >
                Payment Request
              </label>
              <input
                id="resend-payment-request"
                type="text"
                value={resendPaymentRequestTemplateId}
                onChange={e => setResendPaymentRequestTemplateId(e.target.value)}
                placeholder="Resend Template ID — e.g. payment-request"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight font-mono placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <label
                htmlFor="resend-plan-confirmation"
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1 mt-4"
              >
                Plan Confirmation
              </label>
              <input
                id="resend-plan-confirmation"
                type="text"
                value={resendPlanConfirmationTemplateId}
                onChange={e => setResendPlanConfirmationTemplateId(e.target.value)}
                placeholder="Resend Template ID — e.g. plan-confirmation"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight font-mono placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <label
                htmlFor="resend-plan-cancellation"
                className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1 mt-4"
              >
                Plan Cancellation
              </label>
              <input
                id="resend-plan-cancellation"
                type="text"
                value={resendPlanCancellationTemplateId}
                onChange={e => setResendPlanCancellationTemplateId(e.target.value)}
                placeholder="Resend Template ID — e.g. plan-cancellation"
                className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight font-mono placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 h-11 rounded-lg border border-stone text-small text-slate font-semibold
                         hover:border-midnight hover:text-midnight active:bg-mist
                         transition-colors duration-150"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 h-11 rounded-lg bg-plum text-white text-small font-semibold
                         hover:bg-rose active:opacity-80 disabled:opacity-50
                         transition-colors duration-150 flex items-center justify-center gap-2"
            >
              {saving ? 'Saving…' : <><Check size={16} />Save</>}
            </button>
          </div>
        </div>
      )}

      {/* Warning modal before entering edit mode */}
      {showWarning && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
          <div className="absolute inset-0 bg-midnight/50" onClick={() => setShowWarning(false)} aria-hidden="true" />
          <div
            className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10
                       md:rounded-2xl md:max-w-sm md:w-full md:pb-6"
          >
            <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                <AlertTriangle size={20} className="text-amber-600" />
              </div>
              <h2 className="font-display text-h3 text-midnight">Careful!</h2>
            </div>
            <p className="text-[13px] text-slate leading-snug mb-5">
              These settings control how the app integrates with external services.
              Incorrect values can break PDF generation, email delivery, and other
              critical features. Only continue if you know exactly what you are changing.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowWarning(false)}
                className="flex-1 h-11 rounded-xl border border-stone text-small text-slate font-semibold
                           hover:border-midnight hover:text-midnight transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmEdit}
                className="flex-1 h-11 rounded-xl bg-plum text-white text-small font-semibold
                           hover:bg-rose active:opacity-80 transition-colors"
              >
                Continue to Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk upload modal */}
      {showBulkModal && <BulkUploadModal onClose={() => setShowBulkModal(false)} />}
    </div>
  );
}
